import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PublicBlogHeader from '../../components/public/PublicBlogHeader';
import PublicBlogFooter from '../../components/public/PublicBlogFooter';
import { fetchLanding, fetchPublicBlog } from '../../api/public';
import usePageMeta from '../../hooks/usePageMeta';

const stripScripts = (html) =>
  String(html || '')
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/on\w+="[^"]*"/gi, '');

const extractIframeSrc = (value) => {
  const match = String(value || '').match(/<iframe[^>]*src=["']([^"']+)["']/i);
  return match ? match[1] : null;
};

const extractYouTubeId = (raw) => {
  if (!raw) return null;
  const source = extractIframeSrc(raw) || String(raw).trim();

  try {
    const parsed = new URL(source);
    const host = parsed.hostname.toLowerCase();

    if (host.includes('youtu.be')) {
      return parsed.pathname.replace('/', '').trim() || null;
    }

    if (host.includes('youtube.com')) {
      if (parsed.pathname.startsWith('/embed/')) {
        return parsed.pathname.split('/embed/')[1] || null;
      }

      if (parsed.pathname.startsWith('/watch')) {
        return parsed.searchParams.get('v');
      }

      if (parsed.pathname.startsWith('/shorts/')) {
        return parsed.pathname.split('/shorts/')[1] || null;
      }
    }
  } catch (_error) {
    // Ignore parse errors.
  }

  const looseMatch = String(source).match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{6,})/i
  );

  return looseMatch?.[1] || null;
};

const normalizeYouTubeUrl = (raw) => {
  const id = extractYouTubeId(raw);
  if (id) {
    return `https://www.youtube.com/embed/${id}`;
  }
  return null;
};

const normalizeYouTubeWatchUrl = (raw) => {
  const id = extractYouTubeId(raw);
  if (id) {
    return `https://www.youtube.com/watch?v=${id}`;
  }
  return null;
};

const looksLikeHtml = (value) => /<[^>]+>/.test(String(value || ''));

const PostDetailPage = () => {
  const { identifier } = useParams();
  const [loading, setLoading] = useState(true);
  const [blog, setBlog] = useState(null);
  const [allBlogs, setAllBlogs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [navigation, setNavigation] = useState([]);
  const [copyState, setCopyState] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [blogRes, landingRes] = await Promise.all([
          fetchPublicBlog(identifier),
          fetchLanding(),
        ]);
        setBlog(blogRes.data || null);
        setAllBlogs(landingRes.data?.blogs || []);
        setSettings(landingRes.data?.settings || null);
        setNavigation(landingRes.data?.navigation || []);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [identifier]);

  const related = useMemo(() => {
    if (!blog) return [];

    const category = String(blog.category || '').toLowerCase();
    const tagSet = new Set((blog.blogTags || []).map((tag) => String(tag).toLowerCase()));

    return allBlogs
      .filter((item) => item.id !== blog.id)
      .filter((item) => {
        const sameCategory = category && String(item.category || '').toLowerCase() === category;
        const sharesTag = (item.blogTags || []).some((tag) => tagSet.has(String(tag).toLowerCase()));
        return sameCategory || sharesTag;
      })
      .slice(0, 4);
  }, [allBlogs, blog]);

  const embeddedVideoUrl = useMemo(() => {
    if (!blog) return null;

    return (
      normalizeYouTubeUrl(blog.vlogEmbed) ||
      normalizeYouTubeUrl(blog.vlogURL) ||
      normalizeYouTubeUrl(blog.vlogContent) ||
      normalizeYouTubeUrl(blog.content)
    );
  }, [blog]);

  const watchVideoUrl = useMemo(() => {
    if (!blog) return null;

    return (
      normalizeYouTubeWatchUrl(blog.vlogEmbed) ||
      normalizeYouTubeWatchUrl(blog.vlogURL) ||
      normalizeYouTubeWatchUrl(blog.vlogContent) ||
      normalizeYouTubeWatchUrl(blog.content)
    );
  }, [blog]);

  const richHtml = useMemo(() => {
    if (!blog) return '';
    return stripScripts(blog.content || blog.vlogContent || '');
  }, [blog]);

  const shareLinks = useMemo(() => {
    if (typeof window === 'undefined' || !blog) {
      return { email: '#', linkedin: '#', x: '#', facebook: '#', whatsapp: '#', reddit: '#' };
    }

    const url = window.location.href;
    const subject = encodeURIComponent(blog.title);
    const body = encodeURIComponent(`Check out this blog: ${url}`);
    const linkedInUrl = encodeURIComponent(url);
    const xText = encodeURIComponent(`${blog.title} ${url}`);
    const fbUrl = encodeURIComponent(url);
    const whatsappText = encodeURIComponent(`${blog.title} ${url}`);
    const redditTitle = encodeURIComponent(blog.title);

    return {
      email: `mailto:?subject=${subject}&body=${body}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${linkedInUrl}`,
      x: `https://twitter.com/intent/tweet?text=${xText}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${fbUrl}`,
      whatsapp: `https://wa.me/?text=${whatsappText}`,
      reddit: `https://www.reddit.com/submit?url=${fbUrl}&title=${redditTitle}`,
    };
  }, [blog]);

  const canonicalPath = blog ? `/blogs/${blog.blogURL || identifier}` : `/blogs/${identifier}`;
  const seoImage =
    blog?.coverImg ||
    settings?.public_logo_url ||
    'https://res.cloudinary.com/dvh84sf6c/image/upload/v1771230442/EnvisionWealthPlanningLogo-Icon_1_ebp5wf.jpg';
  const structuredData = useMemo(() => {
    const siteName = settings?.site_title || 'Envision Wealth Planning';
    const canonicalUrl =
      typeof window !== 'undefined' ? `${window.location.origin}${canonicalPath}` : canonicalPath;

    if (!blog) {
      return {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: `Blog | ${siteName}`,
        url: canonicalUrl,
      };
    }

    return {
      '@context': 'https://schema.org',
      '@type': blog.blogType === 'video' ? 'VideoObject' : 'Article',
      headline: blog.title,
      description: blog.summary || 'Read practical wealth planning insights from Envision Wealth Planning.',
      image: seoImage,
      datePublished: blog.publishDate || undefined,
      author: {
        '@type': 'Organization',
        name: blog.author || siteName,
      },
      publisher: {
        '@type': 'Organization',
        name: siteName,
        logo: {
          '@type': 'ImageObject',
          url: settings?.public_logo_url || seoImage,
        },
      },
      mainEntityOfPage: canonicalUrl,
      url: canonicalUrl,
      embedUrl: blog.blogType === 'video' ? embeddedVideoUrl || undefined : undefined,
    };
  }, [blog, canonicalPath, embeddedVideoUrl, seoImage, settings]);

  usePageMeta({
    title: blog?.title ? `${blog.title} | ${settings?.site_title || 'Envision Wealth Planning'}` : 'Blog | Envision Wealth Planning',
    description: blog?.summary || 'Read practical wealth planning insights from Envision Wealth Planning.',
    canonicalUrl: canonicalPath,
    image: seoImage,
    type: blog?.blogType === 'video' ? 'video.other' : 'article',
    structuredData,
  });

  const onCopyLink = async () => {
    if (typeof window === 'undefined') return;
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyState('Link copied');
    } catch (_error) {
      setCopyState('Copy failed');
    }
  };

  const onNativeShare = async () => {
    if (typeof window === 'undefined' || !navigator.share || !blog) return;
    try {
      await navigator.share({
        title: blog.title,
        text: blog.summary || blog.title,
        url: window.location.href,
      });
    } catch (_error) {
      // Ignore cancellation.
    }
  };

  if (loading) {
    return <div className="page-loading">Loading blog...</div>;
  }

  if (!blog) {
    return (
      <div className="detail-page">
        <h1>Blog not found</h1>
        <Link to="/">Back to home</Link>
      </div>
    );
  }

  return (
    <div className="public-page compact-public">
      <PublicBlogHeader settings={settings} navigation={navigation} />
      <article className="detail-page rich-detail">
        <div className="detail-main">
          <Link to="/all-blogs" className="detail-back">
            ← Back to all blogs
          </Link>
          <h1>{blog.title}</h1>
          <p className="meta">
            {blog.author || 'Envision Team'} • {blog.blogType} • {blog.category || 'General'}
          </p>

          {blog.coverImg ? <img className="detail-cover" src={blog.coverImg} alt={blog.title} loading="eager" decoding="async" /> : null}

          {blog.blogType === 'video' && embeddedVideoUrl ? (
            <div className="video-wrap">
              <iframe
                title={blog.title}
                src={embeddedVideoUrl}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : null}

          {watchVideoUrl ? (
            <a href={watchVideoUrl} target="_blank" rel="noreferrer" className="watch-on-youtube">
              Open video on YouTube
            </a>
          ) : null}

          {blog.blogType === 'video' && !embeddedVideoUrl && blog.vlogURL ? (
            <video className="video-file-player" src={blog.vlogURL} controls preload="metadata" />
          ) : null}

          <p className="detail-summary">{blog.summary}</p>
          {looksLikeHtml(richHtml) ? (
            <div className="detail-content rich-html" dangerouslySetInnerHTML={{ __html: richHtml }} />
          ) : (
            <div className="detail-content">{richHtml}</div>
          )}
        </div>

        <aside className="detail-sidebar">
          <section className="share-box">
            <h3>Share</h3>
            <div className="share-links">
              <a href={shareLinks.email} title="Share via Email">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 4-8 5-8-5V6l8 5 8-5v2Z"/></svg>
              </a>
              <a href={shareLinks.linkedin} target="_blank" rel="noreferrer" title="Share on LinkedIn">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3A2 2 0 0 1 21 5v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14Zm-1 15v-5.5A3.5 3.5 0 0 0 14.5 9a2.83 2.83 0 0 0-2.5 1.4V9.25H9.5V18H12v-4.75c0-1.05.95-1.9 2-1.9s1.75.85 1.75 1.9V18H18Zm-9.75-8.5A1.75 1.75 0 1 0 6.5 7.75 1.75 1.75 0 0 0 8.25 9.5ZM7 18h2.5V9.25H7V18Z"/></svg>
              </a>
              <a href={shareLinks.x} target="_blank" rel="noreferrer" title="Share on X">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z"/></svg>
              </a>
              <a href={shareLinks.facebook} target="_blank" rel="noreferrer" title="Share on Facebook">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987H7.9V12h2.538V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12Z"/></svg>
              </a>
              <a href={shareLinks.whatsapp} target="_blank" rel="noreferrer" title="Share on WhatsApp">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
              </a>
              <a href={shareLinks.reddit} target="_blank" rel="noreferrer" title="Share on Reddit">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0Zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701ZM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249Zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249Zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095Z"/></svg>
              </a>
              <button type="button" onClick={onCopyLink} title={copyState || 'Copy Link'}>
                {copyState === 'Link copied' ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17Z"/></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4a2 2 0 0 0-2 2v14h2V3h12V1Zm3 4H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2Zm0 16H8V7h11v14Z"/></svg>
                )}
              </button>
              {typeof navigator !== 'undefined' && navigator.share ? (
                <button type="button" onClick={onNativeShare} title="Share">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81a3 3 0 0 0 3-3 3 3 0 0 0-3-3 3 3 0 0 0-3 3c0 .24.04.47.09.7L8.04 9.81A2.99 2.99 0 0 0 6 9a3 3 0 0 0-3 3 3 3 0 0 0 3 3 2.99 2.99 0 0 0 2.04-.81l7.12 4.15c-.05.21-.08.43-.08.66a2.92 2.92 0 0 0 2.92 2.92 2.92 2.92 0 0 0 2.92-2.92A2.92 2.92 0 0 0 18 16.08Z"/></svg>
                </button>
              ) : null}
            </div>
            {copyState ? <p className="share-note">{copyState}</p> : null}
          </section>

          <section className="related-box">
            <h3>Related Articles</h3>
            <div className="related-list">
              {related.map((item) => (
                <Link key={item.id} to={`/blogs/${item.blogURL || item.id}`} className="related-item">
                  <strong>{item.title}</strong>
                  <span>{item.category || 'General'}</span>
                </Link>
              ))}
              {!related.length ? <p className="empty-state">No related items yet.</p> : null}
            </div>
          </section>
        </aside>
      </article>

      <PublicBlogFooter settings={settings} navigation={navigation} />
    </div>
  );
};

export default PostDetailPage;
